/* eslint-disable */
import { useEffect, useState } from 'react'
import { useModal } from '../../hooks/use-modal-store'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'

import useMediaQuery from '../../hooks/useMediaQuery'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '../ui/table'
import env from '../../env/env'
import { toCurrency } from '../../utils/CurrencyUtil'
import { cn } from '../../lib/utils'

const BREAKDOWN_TYPES = {
  STATIC: 'static',
  DYNAMIC: 'dynamic',
}

const BreakdownSelector = ({ selected, onSelect, className }) => (
  <div className={cn('flex items-stretch', className)}>
    <button
      type='button'
      onClick={() => onSelect(BREAKDOWN_TYPES.STATIC)}
      className={cn(
        'flex-1 rounded-l-xl border p-4 text-left transition-all',
        selected === BREAKDOWN_TYPES.STATIC
          ? 'border-primary-500 bg-[#F4F7FE] shadow-sm z-[1]'
          : 'border-[#E8ECF4] bg-white hover:border-[#C5D0E6]',
      )}
    >
      <p className='font-semibold text-sm text-[#2D2D2D]'>Static Allocation</p>
      <p className='text-xs text-black-400 mt-1 font-medium'>Fixed weightage breakdown</p>
    </button>

    <div className='w-px shrink-0 bg-[#E8ECF4] self-stretch' />

    <button
      type='button'
      onClick={() => onSelect(BREAKDOWN_TYPES.DYNAMIC)}
      className={cn(
        'flex-1 rounded-r-xl border p-4 text-left transition-all',
        selected === BREAKDOWN_TYPES.DYNAMIC
          ? 'border-primary-500 bg-[#F4F7FE] shadow-sm z-[1]'
          : 'border-[#E8ECF4] bg-white hover:border-[#C5D0E6]',
      )}
    >
      <p className='font-semibold text-sm text-[#2D2D2D]'>Dynamic Allocation</p>
      <p className='text-xs text-black-400 mt-1 font-medium'>Market-adjusted breakdown</p>
    </button>
  </div>
)

const DesktopBreakdownTable = ({ constituents, totalAmount, calculateParcentageAmount }) => (
  <Table>
    <TableHeader className='bg-[#F4F7FE]'>
      <TableRow className='border-none'>
        <TableHead className='text-primary-400 w-[50%] pl-12 rounded-l-xl'>Fund Name</TableHead>
        <TableHead className='text-primary-400 text-right rounded-r-xl'>Allocation</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {constituents?.map((fund, index) => (
        <TableRow key={index}>
          <TableCell className='font-medium flex gap-x-2 max-w-[432px]'>
            <img
              src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
              alt={`${fund.amcCode} icon`}
              className='rounded-md h-12 w-12'
            />
            <div>
              <p className='text-clip font-semibold text-base text-nowrap truncate'>
                {fund.legalName ? fund.legalName : fund.schemeName}
              </p>
              <span className='font-medium text-xs text-black-400'>{fund.assetClass}</span>
            </div>
          </TableCell>
          <TableCell className='font-semibold text-right'>
            ₹ {fund?.allocationAmount ? toCurrency(fund?.allocationAmount) : toCurrency(calculateParcentageAmount(fund.weightage, totalAmount))}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

const MobileBreakdownTable = ({ constituents, totalAmount, calculateParcentageAmount }) => (
  <Table className='mt-6'>
    <TableBody>
      {constituents?.map((fund, index) => (
        <TableRow
          key={index}
          className={`${index + 1 === constituents?.length && 'border-none'}`}
        >
          <TableCell className='font-medium flex gap-x-2 px-4'>
            <img
              src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
              alt={`${fund.amcCode} icon`}
              className='rounded-md h-12 w-12'
            />
            <div className='w-full'>
              <p className='font-semibold text-sm text-nowrap truncate max-w-[284px]'>
                {fund.legalName ? fund.legalName : fund.schemeName}
              </p>
              <div className='flex justify-between'>
                <span className='font-medium text-xs text-black-400'>{fund.assetClass}</span>
                <span className='font-semibold text-right text-sm'>
                  ₹ {fund?.allocationAmount ? toCurrency(fund?.allocationAmount) : toCurrency(calculateParcentageAmount(fund.weightage, totalAmount))}
                </span>
              </div>
            </div>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

const OpenAdminMFBreakdown = () => {
  const { isOpen, onClose, type, data } = useModal()


  const isModalOpen = isOpen && type === 'OpenAdminMFBreakdown'
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const [selectedBreakdown, setSelectedBreakdown] = useState(BREAKDOWN_TYPES.STATIC)

  useEffect(() => {
    if (isModalOpen) {
      setSelectedBreakdown(BREAKDOWN_TYPES.STATIC)
    }
  }, [isModalOpen])

  const totalAmount = data && data?.amount

  const staticConstituents = data?.staticConstituents || data?.constituents
  const dynamicConstituents = data?.dynamicConstituents || data?.constituents
  const hasDualBreakdown =
    data?.hasDualBreakdown || Boolean(data?.staticConstituents && data?.dynamicConstituents)

  const activeConstituents =
    hasDualBreakdown && selectedBreakdown === BREAKDOWN_TYPES.DYNAMIC
      ? dynamicConstituents
      : staticConstituents

  const calculateParcentageAmount = (parcentage, totalAmount) => {
    const result = totalAmount * (parcentage / 100)
    return result
  }

  return (
    <>
      {isDesktop ? (
        <Dialog open={isModalOpen} onOpenChange={() => onClose()}>
          <DialogContent className='sm:max-w-[736px] font-DMSans p-6' showclose={true}>
            <DialogHeader>
              <DialogTitle className='text-start font-semibold text-xl text-[#2D2D2D]'>
                Detailed Investment Breakdown
              </DialogTitle>
              <DialogDescription className='text-start text-black-400 text-sm font-DMSans font-medium'>
                This is how your investment of{' '}
                <b className='text-black-500'>₹ {toCurrency(totalAmount)}</b> gets divided
              </DialogDescription>
            </DialogHeader>

            {hasDualBreakdown && (
              <BreakdownSelector
                selected={selectedBreakdown}
                onSelect={setSelectedBreakdown}
                className='mb-2'
              />
            )}

            <div>
              <DesktopBreakdownTable
                constituents={activeConstituents}
                totalAmount={totalAmount}
                calculateParcentageAmount={calculateParcentageAmount}
              />
            </div>

            <DialogFooter>
              <Button
                className='border rounded-lg bg-white hover:bg-white text-primary-500'
                onClick={onClose}
              >
                Okay
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={isModalOpen} onOpenChange={() => onClose()}>
          <SheetContent
            className={`font-DMSans p-0 py-5 rounded-t-[24px] 
              ${data?.displayNavbar ? 'bottom-[80px]' : 'bottom-0'}
              `}
            showClose={true}
            side='bottom'
          >
            <SheetHeader className='px-4'>
              <SheetTitle className='text-start font-semibold text-[#2D2D2D]'>
                Detailed Investment Breakdown
              </SheetTitle>
              <SheetDescription className='text-start text-black-400 font-medium text-sm'>
                This is how your investment of{' '}
                <b className='text-black-500'>₹ {toCurrency(totalAmount)}</b> gets divided
              </SheetDescription>
            </SheetHeader>

            {hasDualBreakdown && (
              <BreakdownSelector
                selected={selectedBreakdown}
                onSelect={setSelectedBreakdown}
                className='mt-4 px-4'
              />
            )}

            <MobileBreakdownTable
              constituents={activeConstituents}
              totalAmount={totalAmount}
              calculateParcentageAmount={calculateParcentageAmount}
            />
          </SheetContent>
        </Sheet>
      )}
    </>
  )
}

export default OpenAdminMFBreakdown
